import isNil from 'lodash-es/isNil';
import { BloomreachProductType } from '../hooks/use-search';
import type { SearchParams } from '../hooks/use-search';

export const DEFAULT_PAGE_SIZE = 24;

type ConvertURLParamsToSearchParamsArgs = {
    urlParams: URLSearchParams;
    categoryId?: string;
    productType?: BloomreachProductType;
};
/**
 * Converts URLSearchParams to SearchParams object for search functionality
 * @param urlParams - The URLSearchParams object containing search parameters from URL
 * @returns SearchParams object with typed search parameters
 *
 * The function handles the following parameters:
 * - productType: The type of product to search for (defaults to hardgood)
 * - q: Search query string
 * - categoryId: Category identifier for a category page
 * - price: Array of 2 price values for range
 * - rating: Array of 2 rating values for range
 * - brands: Array of brand names
 * - colors: Array of color values
 * - sizes: Array of size values
 * - materials: Array of material values
 * - sort: Sort order
 * - pageSize: Number of items per page (defaults to DEFAULT_PAGE_SIZE)
 * - page: Page number (defaults to 0)
 * - category: Category filter for a search
 */

export function convertURLParamsToSearchParams({
    urlParams,
    productType,
    categoryId,
}: ConvertURLParamsToSearchParamsArgs): SearchParams {
    const params: SearchParams = {
        ...(productType ? { productType } : {}),
        ...(categoryId ? { categoryId } : {}),
    };

    const q = urlParams.get('q');
    if (q) params.q = q.trim();

    const categoryIdParam = urlParams.get('categoryId');
    if (categoryIdParam) params.categoryId = categoryIdParam;

    const priceValues = urlParams.getAll('price');
    if (priceValues.length) params.price = priceValues;

    const rating = urlParams.get('rating')?.split('-');
    if (rating?.length === 2) params.rating = rating;

    const savings = urlParams.getAll('saving');
    if (savings.length) params.savings = savings;

    const brands = urlParams.getAll('brand');
    if (brands.length) params.brands = brands;

    const colors = urlParams.getAll('color');
    if (colors.length) params.colors = colors;

    const sizes = urlParams.getAll('size');
    if (sizes.length) params.sizes = sizes;

    const materials = urlParams.getAll('material');
    if (materials.length) params.materials = materials;

    const categories = urlParams.getAll('category');
    if (categories.length) params.categories = categories;

    const bopisStoreId = urlParams.get('bopisStoreId');
    if (bopisStoreId) params.bopisStoreId = bopisStoreId;

    const sddStoreId = urlParams.get('sddStoreId');
    if (sddStoreId) params.sddStoreId = sddStoreId;

    const sort = urlParams.get('sort');
    if (sort) params.sort = sort;

    const pageSize = urlParams.get('pageSize');
    params.pageSize =
        typeof pageSize === 'string' && pageSize ? parseInt(pageSize, 10) : DEFAULT_PAGE_SIZE;

    const page = Number(urlParams.get('page') || '1') || 1;
    params.page = Math.max(page, 1) - 1;

    return params;
}

export const cleanPathname = (pathname: string): string =>
    pathname
        .replace(/\/+/, '/') // Remove multiple slashes
        .replace(/\/+$/, '') // Remove trailing slash
        .replace(/^\/pwa/, '') // Remove pwa prefix
        .replace(/^\/hidden/, ''); // Remove hidden prefix

export const convertSearchParamsToURLParams = (searchParams: SearchParams): URLSearchParams => {
    const urlParams = new URLSearchParams();

    if (searchParams.q) {
        urlParams.set('q', searchParams.q);
    }

    if (searchParams.brands?.length) {
        searchParams.brands.forEach((brand) => urlParams.append('brand', brand));
    }

    if (searchParams.colors?.length) {
        searchParams.colors.forEach((color) => urlParams.append('color', color));
    }

    if (searchParams.sizes?.length) {
        searchParams.sizes.forEach((size) => urlParams.append('size', size));
    }

    if (searchParams.materials?.length) {
        searchParams.materials.forEach((material) => urlParams.append('material', material));
    }

    if (searchParams.price?.length) {
        searchParams.price.forEach((price) => urlParams.append('price', price));
    }

    if (searchParams.rating?.length === 2) {
        urlParams.set('rating', searchParams.rating.join('-'));
    }

    if (searchParams.savings?.length) {
        searchParams.savings.forEach((saving) => urlParams.append('saving', saving));
    }

    if (searchParams.categories?.length) {
        searchParams.categories.forEach((brand) => urlParams.append('category', brand));
    }

    if (searchParams.bopisStoreId) {
        urlParams.set('bopisStoreId', searchParams.bopisStoreId);
    }

    if (searchParams.sddStoreId) {
        urlParams.set('sddStoreId', searchParams.sddStoreId);
    }

    if (searchParams.page) {
        urlParams.set('page', (searchParams.page + 1).toString());
    }

    if (searchParams.pageSize && searchParams.pageSize !== DEFAULT_PAGE_SIZE) {
        urlParams.set('pageSize', searchParams.pageSize.toString());
    }

    if (searchParams.productType && searchParams.productType !== BloomreachProductType.hardgood) {
        urlParams.set('productType', searchParams.productType);
    }

    if (searchParams.sort) {
        urlParams.set('sort', searchParams.sort);
    }

    return urlParams;
};

/**
 * Creates URLSearchParams for service API calls from SearchParams
 * @param params - The search parameters object
 * @returns URLSearchParams object for service API calls
 */
export function createServiceURLSearchParams(params: SearchParams): URLSearchParams {
    const urlSearchParams = new URLSearchParams();

    urlSearchParams.append('productType', params.productType || BloomreachProductType.hardgood);

    if (params.q?.trim()) {
        urlSearchParams.append('q', params.q.trim());
    }

    if (params.categoryId) {
        urlSearchParams.append('categoryId', params.categoryId);
    }
    if (params.price?.length) {
        params.price.forEach((price) => urlSearchParams.append('price', price));
    }
    if (params.rating?.length === 2) {
        urlSearchParams.append('rating', params.rating[0]);
        urlSearchParams.append('rating', params.rating[1]);
    }
    if (params.savings?.length) {
        params.savings.filter((x) => x.trim()).forEach((x) => urlSearchParams.append('saving', x));
    }
    if (params.brands?.length) {
        params.brands.filter((x) => x.trim()).forEach((x) => urlSearchParams.append('brand', x));
    }
    if (params.colors?.length) {
        params.colors.filter((x) => x.trim()).forEach((x) => urlSearchParams.append('color', x));
    }
    if (params.sizes?.length) {
        params.sizes.filter((x) => x.trim()).forEach((x) => urlSearchParams.append('size', x));
    }
    if (params.materials?.length) {
        params.materials
            .filter((x) => x.trim())
            .forEach((x) => urlSearchParams.append('material', x));
    }
    if (params.categories?.length) {
        params.categories
            .filter((x) => x.trim())
            .forEach((x) => urlSearchParams.append('category', x));
    }
    if (params.bopisStoreId) {
        urlSearchParams.append('bopisStoreId', params.bopisStoreId);
    }
    if (params.sddStoreId) {
        urlSearchParams.append('sddStoreId', params.sddStoreId);
    }
    if (params.sort) {
        urlSearchParams.append('sort', params.sort);
    }

    urlSearchParams.append(
        'pageSize',
        params.pageSize ? params.pageSize.toString() : DEFAULT_PAGE_SIZE.toString(),
    );

    urlSearchParams.append('page', params.page?.toString() || '0');

    return urlSearchParams;
}

export const isEmptySearchParams = (searchParams: SearchParams): boolean =>
    Object.keys(searchParams)
        .filter(
            (key) => !['productType', 'q', 'categoryId', 'sort', 'page', 'pageSize'].includes(key),
        )
        .every((key) => {
            const value = searchParams[key as keyof SearchParams];

            return Array.isArray(value) ? value.filter((x) => x.trim()).length === 0 : isNil(value);
        });
